import InputOptions from "./InputOptions";
import { SVGs } from "../assets";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Constants } from "../utils";

export type LanguagePickerOption<T extends string = string> = {
  code: T;
  label: string;
  countryIso2: string;
};

export type LanguagePickerProps<T extends string = string> = {
  options: LanguagePickerOption[];
  value: T;
  onChange: (value: T) => void;
  label?: string;
  className?: string;
};

export default function LanguagePicker<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: LanguagePickerProps<T>) {
  const { t } = useTranslation();
  const ariaLabel = label ?? t("general.language");

  const selected = React.useMemo(() => {
    const match = options.find((o) => o.code === value);
    return match ?? options[0];
  }, [options, value]);

  return (
    <InputOptions
      className={className}
      position="right"
      value={value}
      onChange={(v) => onChange(v as T)}
      options={options.map((opt) => ({
        value: opt.code,
        label: opt.label,
      }))}
    >
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        className="inline-flex h-[40px] items-center gap-[14px] rounded-[30px] bg-secondary px-[6px]"
      >
        <div className="relative h-[28.5px] w-[28.5px] bg-surface rounded-full overflow-hidden">
          <img
            alt={selected.code}
            src={Constants.flagSrc(selected.countryIso2)}
            aria-hidden="true"
            className="h-full w-full object-cover"
          />
        </div>
        <SVGs.ChevronDown className="w-[12.86px] mr-[4px] text-text-surface" />
      </button>
    </InputOptions>
  );
}
